/** Shared State Portal marketing branding (localStorage) helpers. */

import { PORTAL_COLORS } from '@/components/refRepsPortal/portalTheme.js';
import refrepsShieldLogo from '@/assets/refreps-shield-logo.png';
export {
    STATE_PORTAL_COLOR_LOG,
    STATE_PORTAL_COLOR_ROLE_DEFS,
    listStatePortalColorRoles,
    describeStatePortalColorRole,
} from '@/components/Instructor/StatePortal/statePortalColorLog.js';

export const STATE_PORTAL_BRANDING_UPDATED = 'state-portal-branding-updated';
export const STATE_PORTAL_OPEN_BRANDING = 'state-portal-open-branding';

const THEME_SCHEME_FIXED = 'fixed';
const THEME_SCHEME_GRADIENT = 'gradient';
const MIN_BRAND_PALETTE_COLORS = 2;
const MAX_BRAND_PALETTE_COLORS = 6;
const DEFAULT_BRAND_PALETTE = [
    PORTAL_COLORS.teal600,
    PORTAL_COLORS.teal500,
    PORTAL_COLORS.brandCyan,
    PORTAL_COLORS.teal400,
    PORTAL_COLORS.teal700,
    PORTAL_COLORS.brandCyanLight,
];

/**
 * Spectrum families remapped onto the branding scale while State Portal is active.
 * Warning / danger keep their native hues (see SEMANTIC families below).
 */
export const STATE_PORTAL_BRAND_SPECTRUM_FAMILIES = [
    'lime', 'green', 'emerald', 'teal',
    'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple', 'fuchsia', 'pink',
];

/** Keep native pattern colors: red/rose (danger), amber/yellow/orange (warning). */
export const STATE_PORTAL_SEMANTIC_SPECTRUM_FAMILIES = [
    'red', 'rose', 'amber', 'yellow', 'orange',
];

export const STATE_PORTAL_SPECTRUM_FAMILIES = [
    ...STATE_PORTAL_BRAND_SPECTRUM_FAMILIES,
    ...STATE_PORTAL_SEMANTIC_SPECTRUM_FAMILIES,
];

export const STATE_PORTAL_SPECTRUM_SHADES = [
    '50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950',
];

/** Tailwind default RGB channels for warning/danger families (space-separated). */
const SEMANTIC_SPECTRUM_DEFAULT_CHANNELS = {
    red: {
        50: '254 242 242', 100: '254 226 226', 200: '254 202 202', 300: '252 165 165',
        400: '248 113 113', 500: '239 68 68', 600: '220 38 38', 700: '185 28 28',
        800: '153 27 27', 900: '127 29 29', 950: '69 10 10',
    },
    rose: {
        50: '255 241 242', 100: '255 228 230', 200: '254 205 211', 300: '253 164 175',
        400: '251 113 133', 500: '244 63 94', 600: '225 29 72', 700: '190 18 60',
        800: '159 18 57', 900: '136 19 55', 950: '76 5 25',
    },
    amber: {
        50: '255 251 235', 100: '254 243 199', 200: '253 230 138', 300: '252 211 77',
        400: '251 191 36', 500: '245 158 11', 600: '217 119 6', 700: '180 83 9',
        800: '146 64 14', 900: '120 53 15', 950: '69 26 3',
    },
    yellow: {
        50: '254 252 232', 100: '254 249 195', 200: '254 240 138', 300: '253 224 71',
        400: '250 204 21', 500: '234 179 8', 600: '202 138 4', 700: '161 98 7',
        800: '133 77 14', 900: '113 63 18', 950: '66 32 6',
    },
    orange: {
        50: '255 247 237', 100: '255 237 213', 200: '254 215 170', 300: '253 186 116',
        400: '251 146 60', 500: '249 115 22', 600: '234 88 12', 700: '194 65 12',
        800: '154 52 18', 900: '124 45 18', 950: '67 20 7',
    },
};

/** All CSS custom properties applied / cleared for State Portal theming. */
export const STATE_PORTAL_THEME_CSS_VARS = [
    '--sp-brand-fill',
    '--sp-on-white',
    '--sp-on-brand',
    ...STATE_PORTAL_SPECTRUM_FAMILIES.flatMap((family) => (
        STATE_PORTAL_SPECTRUM_SHADES.map((shade) => `--sp-${family}-${shade}`)
    )),
];

/** @deprecated Use STATE_PORTAL_THEME_CSS_VARS */
export const STATE_PORTAL_TEAL_CSS_VARS = STATE_PORTAL_THEME_CSS_VARS;

function channelsToHex(channels = '') {
    const parts = String(channels || '').trim().split(/\s+/).map((part) => Number(part));
    if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return '';
    return `#${parts.slice(0, 3).map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('')}`;
}

function rgbObjectToHex(rgb = null) {
    if (!rgb) return '';
    const toHex = (n) => Math.max(0, Math.min(255, Number(n) || 0)).toString(16).padStart(2, '0');
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/**
 * CSS color value bound to live theme vars (style= / SVG).
 * Example: portalPaintCss('teal', '500') → 'rgb(var(--sp-teal-500))'
 */
export function portalPaintCss(family = 'teal', shade = '500') {
    const safeFamily = STATE_PORTAL_SPECTRUM_FAMILIES.includes(String(family))
        ? String(family)
        : 'teal';
    const safeShade = STATE_PORTAL_SPECTRUM_SHADES.includes(String(shade))
        ? String(shade)
        : '500';
    return `rgb(var(--sp-${safeFamily}-${safeShade}))`;
}

/**
 * Solid hex for chart libraries (ApexCharts, canvas, etc.) that cannot parse CSS variables.
 * Brand families follow the saved palette; warning/danger stay native red/rose/amber/yellow/orange.
 */
export function portalPaintHex(family = 'teal', shade = '500', branding = null) {
    const safeFamily = STATE_PORTAL_SPECTRUM_FAMILIES.includes(String(family))
        ? String(family)
        : 'teal';
    const safeShade = STATE_PORTAL_SPECTRUM_SHADES.includes(String(shade))
        ? String(shade)
        : '500';

    if (STATE_PORTAL_SEMANTIC_SPECTRUM_FAMILIES.includes(safeFamily)) {
        return channelsToHex(SEMANTIC_SPECTRUM_DEFAULT_CHANNELS[safeFamily]?.[safeShade])
            || '#dc2626';
    }

    if (branding && typeof branding === 'object') {
        const scale = buildStatePortalTealScale(branding.palette, branding.scheme);
        const hex = rgbObjectToHex(scale[safeShade]);
        if (hex) return hex;
    }

    if (typeof document !== 'undefined') {
        const raw = window.getComputedStyle(document.documentElement)
            .getPropertyValue(`--sp-${safeFamily}-${safeShade}`)
            .trim();
        const fromDom = channelsToHex(raw);
        if (fromDom) return fromDom;
    }

    const fallbackScale = buildStatePortalTealScale(DEFAULT_BRAND_PALETTE, THEME_SCHEME_FIXED);
    return rgbObjectToHex(fallbackScale[safeShade]) || PORTAL_COLORS.teal600;
}

/**
 * Brand-scale palette for multi-series charts / tables.
 * Prefers saved palette paints, then fills from the derived teal shade ladder.
 */
export function portalChartColors(count = 1, branding = null) {
    const size = Math.max(1, Number(count) || 1);
    const palette = clampPalette(branding?.palette || DEFAULT_BRAND_PALETTE);
    const safePalette = contrastSafePaletteHexes(palette, 3);
    const shadeLadder = ['600', '500', '400', '700', '300', '800', '200', '900'];
    const colors = [];

    for (let i = 0; i < size; i += 1) {
        if (i < safePalette.length) {
            colors.push(safePalette[i] || portalPaintHex('teal', shadeLadder[i % shadeLadder.length], branding));
        } else {
            colors.push(portalPaintHex('teal', shadeLadder[i % shadeLadder.length], branding));
        }
    }
    return colors;
}

function slugPart(value = '') {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'general';
}

function normalizeHex(value = '') {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const withHash = raw.startsWith('#') ? raw : `#${raw}`;
    if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
        return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`.toLowerCase();
    }
    if (/^#[0-9a-fA-F]{6}$/.test(withHash)) {
        return withHash.toLowerCase();
    }
    return '';
}

function normalizeThemeScheme(value = '') {
    return String(value || '').trim().toLowerCase() === THEME_SCHEME_GRADIENT
        ? THEME_SCHEME_GRADIENT
        : THEME_SCHEME_FIXED;
}

function clampPalette(list = []) {
    const cleaned = (Array.isArray(list) ? list : [])
        .map((entry) => normalizeHex(entry))
        .filter(Boolean)
        .slice(0, MAX_BRAND_PALETTE_COLORS);
    while (cleaned.length < MIN_BRAND_PALETTE_COLORS) {
        cleaned.push(DEFAULT_BRAND_PALETTE[cleaned.length] || PORTAL_COLORS.teal600);
    }
    return cleaned;
}

export function resolveOrganizationName(organization = {}) {
    return String(organization?.name || '').trim();
}

/**
 * Display name for the State Portal itself (sidebar + branding).
 * Prefer the selected state-portal organization / state association — never the
 * instructor's personal Learn organization.
 */
export function resolveStatePortalOrganizationName(mainStore = {}, state = '') {
    const stateKey = String(
        state || mainStore?.currentStatePortal?.abbreviation || '',
    ).trim().toUpperCase();
    const geoName = String(mainStore?.currentStatePortal?.name || '').trim();
    if (!stateKey) return geoName;

    const selectorEntries = Array.isArray(mainStore?.statePortalSelectorEntries)
        ? mainStore.statePortalSelectorEntries
        : [];
    if (selectorEntries.length) {
        const match = selectorEntries.find((entry) => {
            const entryState = String(entry?.state || '').trim().toUpperCase();
            return entryState === stateKey;
        });
        const selectorName = String(match?.name || '').trim();
        // Multi-org selector rows use the org name; single-org rows use the
        // geographic state name — skip those so the association name wins.
        if (
            selectorName
            && selectorName !== geoName
            && selectorName.toUpperCase() !== stateKey
        ) {
            return selectorName;
        }
    }

    const association = (Array.isArray(mainStore?.stateAssociations)
        ? mainStore.stateAssociations
        : []
    ).find((entry) => String(entry?.abbreviation || '').trim().toUpperCase() === stateKey);
    const associationName = String(association?.name || '').trim();
    if (associationName) return associationName;

    return geoName;
}

export function brandingStorageKey(orgId = '', state = '') {
    const id = String(orgId || 'org').trim() || 'org';
    return `statePortal.marketingBranding.${id}.${slugPart(state)}`;
}

export function refrepsBrandingRecord() {
    const profile = {
        name: 'RefReps',
        acronym: 'RR',
        logoUrl: refrepsShieldLogo,
        website: 'https://www.refreps.com',
        websites: [{ title: 'RefReps', url: 'https://www.refreps.com' }],
        phone: '',
        phones: [],
        email: '',
        emails: [],
        guideName: 'RefReps Brand Guide',
        guideFileName: '',
        guideFileUrl: '',
        guideFileCount: 0,
        restrictionsList: [],
        restrictionChoices: [],
        restrictions: 'Base RefReps branding — locked for platform consistency until a state sets its own.',
        themeColors: {
            palette: [PORTAL_COLORS.teal600, PORTAL_COLORS.teal500, PORTAL_COLORS.brandCyan],
            scheme: THEME_SCHEME_GRADIENT,
        },
        extraResources: [],
    };
    const portfolio = snapshotBrandingPortfolio({
        id: 'refreps-default-portfolio',
        name: defaultBrandingPortfolioName(0),
        profile,
        connectors: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    });
    return {
        profile,
        connectors: {},
        portfolios: [portfolio],
        activePortfolioId: portfolio.id,
        updatedAt: portfolio.updatedAt,
    };
}

export function ensureRefRepsBrandingIfEmpty(storageKey = '') {
    const key = String(storageKey || '').trim();
    if (!key || typeof window === 'undefined' || !window.localStorage) return null;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw) return null;
    } catch (_) {
        return null;
    }
    return persistBrandingRecord(key, refrepsBrandingRecord());
}

/** Stock / placeholder images that must never be used as the portal display logo. */
const STOCK_PORTAL_LOGO_MARKERS = [
    'baseball/baseball101.jpg',
    'host.refreps.com/images/placeholder-image.png',
];

export function isStockPlaceholderPortalLogo(url = '') {
    const value = String(url || '').trim().toLowerCase();
    if (!value) return false;
    return STOCK_PORTAL_LOGO_MARKERS.some((marker) => value.includes(marker));
}

/** Top-left portal mark: custom logo, otherwise RefReps branding. */
export function resolvePortalDisplayLogoUrl(logoUrl = '') {
    const value = String(logoUrl || '').trim();
    if (!value || isStockPlaceholderPortalLogo(value)) return refrepsShieldLogo;
    return value;
}

function replaceStockLogosInRecord(record) {
    if (!record || typeof record !== 'object') return { record, changed: false };
    let changed = false;
    const next = { ...record };
    if (next.profile && typeof next.profile === 'object' && isStockPlaceholderPortalLogo(next.profile.logoUrl)) {
        next.profile = { ...next.profile, logoUrl: refrepsShieldLogo };
        changed = true;
    }
    if (Array.isArray(next.portfolios)) {
        next.portfolios = next.portfolios.map((portfolio) => {
            if (!portfolio?.profile || typeof portfolio.profile !== 'object') return portfolio;
            if (!isStockPlaceholderPortalLogo(portfolio.profile.logoUrl)) return portfolio;
            changed = true;
            return {
                ...portfolio,
                profile: { ...portfolio.profile, logoUrl: refrepsShieldLogo },
            };
        });
    }
    return { record: next, changed };
}

/** Swap a leftover stock/placeholder logo for the RefReps mark. */
export function ensureRefRepsLogoIfPlaceholder(storageKey = '') {
    const key = String(storageKey || '').trim();
    if (!key || typeof window === 'undefined' || !window.localStorage) return null;
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const { record, changed } = replaceStockLogosInRecord(parsed);
        if (!changed) return parsed;
        return persistBrandingRecord(key, record);
    } catch (_) {
        return null;
    }
}

function cloneBrandingValue(value, fallback) {
    if (value == null) return fallback;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return fallback;
    }
}

export function newBrandingPortfolioId() {
    return `brand-portfolio-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultBrandingPortfolioName(index = 0) {
    return index <= 0 ? 'Your portal branding' : `Branding ${index + 1}`;
}

export function snapshotBrandingPortfolio({
    id,
    name,
    profile,
    connectors,
    createdAt,
    updatedAt,
} = {}) {
    const now = new Date().toISOString();
    return {
        id: String(id || newBrandingPortfolioId()),
        name: String(name || '').trim() || defaultBrandingPortfolioName(0),
        profile: profile && typeof profile === 'object' ? cloneBrandingValue(profile, null) : null,
        connectors: connectors && typeof connectors === 'object' ? cloneBrandingValue(connectors, {}) : {},
        createdAt: createdAt || now,
        updatedAt: updatedAt || now,
    };
}

export function normalizeBrandingPortfolios(parsed = {}) {
    const existing = Array.isArray(parsed?.portfolios)
        ? parsed.portfolios.filter((item) => item && typeof item === 'object')
        : [];

    if (existing.length) {
        const portfolios = existing.map((item, index) => snapshotBrandingPortfolio({
            ...item,
            name: item.name || defaultBrandingPortfolioName(index),
        }));
        const activePortfolioId = portfolios.some((item) => item.id === parsed?.activePortfolioId)
            ? parsed.activePortfolioId
            : portfolios[0].id;
        const active = portfolios.find((item) => item.id === activePortfolioId) || portfolios[0];
        return {
            portfolios,
            activePortfolioId: active.id,
            profile: active.profile,
            connectors: active.connectors && typeof active.connectors === 'object' ? active.connectors : {},
        };
    }

    const hasProfile = parsed?.profile && typeof parsed.profile === 'object';
    const hasConnectors = parsed?.connectors && typeof parsed.connectors === 'object'
        && Object.keys(parsed.connectors).length > 0;
    if (!hasProfile && !hasConnectors) {
        return {
            portfolios: [],
            activePortfolioId: '',
            profile: null,
            connectors: {},
        };
    }

    const first = snapshotBrandingPortfolio({
        id: parsed?.activePortfolioId || newBrandingPortfolioId(),
        name: defaultBrandingPortfolioName(0),
        profile: parsed.profile ?? null,
        connectors: parsed.connectors ?? {},
        createdAt: parsed?.updatedAt,
        updatedAt: parsed?.updatedAt,
    });
    return {
        portfolios: [first],
        activePortfolioId: first.id,
        profile: first.profile,
        connectors: first.connectors,
    };
}

export function persistBrandingRecord(storageKey, {
    profile,
    connectors,
    portfolios,
    activePortfolioId,
} = {}) {
    if (typeof window === 'undefined' || !window.localStorage || !storageKey) return null;
    const normalized = normalizeBrandingPortfolios({
        profile,
        connectors,
        portfolios,
        activePortfolioId,
    });
    const nextPortfolios = normalized.portfolios.map((item) => (
        item.id === normalized.activePortfolioId
            ? snapshotBrandingPortfolio({
                ...item,
                profile: profile !== undefined ? profile : item.profile,
                connectors: connectors !== undefined ? connectors : item.connectors,
                updatedAt: new Date().toISOString(),
            })
            : item
    ));
    const active = nextPortfolios.find((item) => item.id === normalized.activePortfolioId) || nextPortfolios[0] || null;
    const payload = {
        profile: active ? (active.profile ?? null) : (profile ?? null),
        connectors: active
            ? (active.connectors && typeof active.connectors === 'object' ? active.connectors : {})
            : (connectors && typeof connectors === 'object' ? connectors : {}),
        portfolios: nextPortfolios,
        activePortfolioId: active?.id || '',
        updatedAt: new Date().toISOString(),
    };
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
        return null;
    }
    return payload;
}

export function resolveBrandingOrgId(mainStore = {}) {
    const organization = mainStore?.organization || {};
    return String(
        organization?.companyID
        || organization?.id
        || organization?._id
        || 'org',
    ).trim() || 'org';
}

function buildGradientCss(colors = [], angle = '90deg') {
    const stops = clampPalette(colors);
    if (stops.length === 1) return stops[0];
    return `linear-gradient(${angle}, ${stops.join(', ')})`;
}

function hexToRgb(hex = '') {
    const normalized = normalizeHex(hex);
    if (!normalized) return null;
    return {
        r: parseInt(normalized.slice(1, 3), 16),
        g: parseInt(normalized.slice(3, 5), 16),
        b: parseInt(normalized.slice(5, 7), 16),
    };
}

function rgbToChannels(rgb) {
    if (!rgb) return '13 148 136';
    return `${rgb.r} ${rgb.g} ${rgb.b}`;
}

function mixRgb(a, b, t = 0.5) {
    const amount = Math.min(1, Math.max(0, Number(t) || 0));
    return {
        r: Math.round(a.r + (b.r - a.r) * amount),
        g: Math.round(a.g + (b.g - a.g) * amount),
        b: Math.round(a.b + (b.b - a.b) * amount),
    };
}

function relativeLuminance(rgb = null) {
    if (!rgb) return 1;
    const toLinear = (channel) => {
        const value = Math.max(0, Math.min(255, Number(channel) || 0)) / 255;
        return value <= 0.03928
            ? value / 12.92
            : ((value + 0.055) / 1.055) ** 2.4;
    };
    return (
        0.2126 * toLinear(rgb.r)
        + 0.7152 * toLinear(rgb.g)
        + 0.0722 * toLinear(rgb.b)
    );
}

function contrastRatioAgainstWhite(rgb = null) {
    const fg = relativeLuminance(rgb);
    const bg = 1; // white
    const lighter = Math.max(fg, bg);
    const darker = Math.min(fg, bg);
    return (lighter + 0.05) / (darker + 0.05);
}

const RGB_WHITE = { r: 255, g: 255, b: 255 };
const RGB_BLACK = { r: 0, g: 0, b: 0 };

function contrastRatio(fg = null, bg = RGB_WHITE) {
    const L1 = relativeLuminance(fg);
    const L2 = relativeLuminance(bg);
    const lighter = Math.max(L1, L2);
    const darker = Math.min(L1, L2);
    return (lighter + 0.05) / (darker + 0.05);
}

/** Darken toward black until readable on the given background. */
function ensureContrastOnBackground(rgb = null, background = RGB_WHITE, minRatio = 4.5) {
    let current = rgb || hexToRgb(PORTAL_COLORS.teal700);
    let guard = 0;
    while (contrastRatio(current, background) < minRatio && guard < 28) {
        current = mixRgb(current, RGB_BLACK, 0.14);
        guard += 1;
    }
    return current;
}

/** Darken a brand paint until it stays readable on white (WCAG AA body/header). */
function ensureContrastOnWhite(rgb = null, minRatio = 4.5) {
    return ensureContrastOnBackground(rgb, RGB_WHITE, minRatio);
}

/**
 * Keep light brand tints/borders visibly distinct from white by mixing in
 * a contrast-safe palette pigment (never blends into the page background).
 */
function ensureDistinctFromWhite(rgb = null, pigment = null, minRatio = 1.2) {
    const basePigment = pigment || hexToRgb(PORTAL_COLORS.teal600);
    let current = rgb || mixRgb(basePigment, RGB_WHITE, 0.85);
    let guard = 0;
    while (contrastRatioAgainstWhite(current) < minRatio && guard < 28) {
        current = mixRgb(current, basePigment, 0.22);
        guard += 1;
    }
    return current;
}

function tintFromPigment(pigment, amountTowardWhite = 0.85) {
    return mixRgb(pigment || hexToRgb(PORTAL_COLORS.teal600), RGB_WHITE, amountTowardWhite);
}

/**
 * Best brand paint for text/icons on white backgrounds.
 * Prefers primary, then deeper palette stops, then a darkened brand tone.
 */
export function pickOnWhiteBrandRgb(palette = []) {
    const paints = clampPalette(palette);
    const candidates = [
        paints[0],
        paints[3],
        paints[1],
        paints[2],
        paints[4],
        paints[5],
        PORTAL_COLORS.teal700,
    ]
        .map((hex) => hexToRgb(hex))
        .filter(Boolean)
        .sort((a, b) => contrastRatioAgainstWhite(b) - contrastRatioAgainstWhite(a));

    const best = candidates[0] || hexToRgb(PORTAL_COLORS.teal700);
    return ensureContrastOnWhite(best, 4.5);
}

/** Text/icon color that contrasts on top of a brand fill (white or dark brand). */
function pickOnBrandTextRgb(fillRgb = null, onWhiteRgb = null) {
    const fill = fillRgb || hexToRgb(PORTAL_COLORS.teal600);
    if (contrastRatio(RGB_WHITE, fill) >= 4.5) return RGB_WHITE;
    return ensureContrastOnBackground(onWhiteRgb || fill, fill, 4.5);
}

function contrastSafePaletteHexes(palette = [], minRatio = 3) {
    return clampPalette(palette).map((hex) => {
        const safe = ensureContrastOnWhite(hexToRgb(hex), minRatio);
        return rgbObjectToHex(safe) || hex;
    });
}

/**
 * Distribute brand palette paints across pigmented UI shade roles.
 * Neutrals stay fixed; every spectrum family aliases this scale on State Portal.
 * Every pigmented shade stays visibly distinct from white and text shades stay readable.
 */
export function buildStatePortalTealScale(palette = [], scheme = THEME_SCHEME_FIXED) {
    const paints = clampPalette(palette);
    const primary = paints[0] || PORTAL_COLORS.teal600;
    const accent = paints[1] || paints[0] || PORTAL_COLORS.teal500;
    const highlight = paints[2] || null;
    const deep = paints[3] || null;
    const soft = paints[4] || null;
    const mist = paints[5] || null;
    const onWhite = pickOnWhiteBrandRgb(paints);

    // All pigmented roles derive from contrast-safe palette paints so nothing
    // washes out into the white State Portal background.
    const textAccent = ensureContrastOnWhite(hexToRgb(accent) || onWhite, 4.5);
    const textPrimary = ensureContrastOnWhite(hexToRgb(primary) || onWhite, 4.5);
    const textDeep = ensureContrastOnWhite(
        deep ? hexToRgb(deep) : mixRgb(textPrimary, RGB_BLACK, 0.18),
        4.5,
    );
    const chrome = ensureContrastOnWhite(
        highlight ? hexToRgb(highlight) : mixRgb(onWhite, RGB_WHITE, 0.22),
        3,
    );

    const scale = {
        50: ensureDistinctFromWhite(tintFromPigment(onWhite, 0.9), onWhite, 1.14),
        100: ensureDistinctFromWhite(tintFromPigment(onWhite, 0.82), onWhite, 1.2),
        200: ensureDistinctFromWhite(
            mist ? hexToRgb(mist) : tintFromPigment(onWhite, 0.66),
            onWhite,
            1.3,
        ),
        300: ensureDistinctFromWhite(
            soft ? hexToRgb(soft) : tintFromPigment(onWhite, 0.48),
            onWhite,
            1.5,
        ),
        400: chrome,
        500: textAccent,
        600: textPrimary,
        700: textDeep,
        800: ensureContrastOnWhite(mixRgb(textPrimary, RGB_BLACK, 0.28), 4.5),
        900: ensureContrastOnWhite(mixRgb(textPrimary, RGB_BLACK, 0.4), 4.5),
        950: ensureContrastOnWhite(mixRgb(textPrimary, RGB_BLACK, 0.55), 4.5),
        onWhite,
        onBrand: pickOnBrandTextRgb(textPrimary, onWhite),
    };

    // Gradient scheme still needs solid role colors for text/borders/rings;
    // pigmented fills use --sp-brand-fill via CSS.
    void scheme;

    return scale;
}

export function buildStatePortalThemeCssVars(branding = {}) {
    const palette = clampPalette(branding.palette);
    const scheme = normalizeThemeScheme(branding.scheme);
    const scale = buildStatePortalTealScale(palette, scheme);
    const safeFillHexes = contrastSafePaletteHexes(palette, 3);
    const brandFill = scheme === THEME_SCHEME_GRADIENT
        ? buildGradientCss(safeFillHexes, '135deg')
        : (safeFillHexes[0] || rgbObjectToHex(scale[600]) || PORTAL_COLORS.teal600);

    const vars = {
        '--sp-brand-fill': brandFill,
        '--sp-on-white': rgbToChannels(scale.onWhite || scale[700] || scale[600]),
        '--sp-on-brand': rgbToChannels(scale.onBrand || RGB_WHITE),
    };

    // Brand chrome families → contrast-safe branding scale.
    STATE_PORTAL_BRAND_SPECTRUM_FAMILIES.forEach((family) => {
        STATE_PORTAL_SPECTRUM_SHADES.forEach((shade) => {
            vars[`--sp-${family}-${shade}`] = rgbToChannels(scale[shade]);
        });
    });

    // Warning / danger keep native red/rose + amber/yellow/orange pattern colors.
    STATE_PORTAL_SEMANTIC_SPECTRUM_FAMILIES.forEach((family) => {
        STATE_PORTAL_SPECTRUM_SHADES.forEach((shade) => {
            vars[`--sp-${family}-${shade}`] = SEMANTIC_SPECTRUM_DEFAULT_CHANNELS[family][shade];
        });
    });

    return vars;
}

/**
 * Load applied portal branding for the current org/state.
 * Name prefers the state organization / association (not the instructor org).
 */
export function loadAppliedStatePortalBranding(mainStore = {}, state = '') {
    const stateKey = String(state || mainStore?.currentStatePortal?.abbreviation || '').trim();
    const orgName = resolveStatePortalOrganizationName(mainStore, stateKey);
    const orgId = resolveBrandingOrgId(mainStore);
    const key = brandingStorageKey(orgId, stateKey);

    let profile = null;
    try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
        if (raw) {
            const migrated = ensureRefRepsLogoIfPlaceholder(key);
            const parsed = migrated || JSON.parse(raw);
            if (parsed?.profile && typeof parsed.profile === 'object') {
                profile = parsed.profile;
            }
        } else {
            const seeded = ensureRefRepsBrandingIfEmpty(key);
            if (seeded?.profile && typeof seeded.profile === 'object') {
                profile = seeded.profile;
            }
        }
    } catch (_) {
        profile = null;
    }

    const palette = clampPalette(profile?.themeColors?.palette);
    const scheme = normalizeThemeScheme(profile?.themeColors?.scheme);
    const logoUrl = resolvePortalDisplayLogoUrl(profile?.logoUrl);
    const hasCustomColors = palette.join(',') !== clampPalette(DEFAULT_BRAND_PALETTE.slice(0, 2)).join(',')
        || scheme !== THEME_SCHEME_FIXED;
    const configured = Boolean(profile && (orgName || logoUrl || hasCustomColors));
    const websites = normalizeBrandingWebsites(profile);
    const phones = normalizeBrandingPhones(profile);
    const emails = normalizeBrandingEmails(profile);

    return {
        configured,
        name: orgName || String(profile?.name || '').trim() || 'State Portal',
        logoUrl,
        acronym: String(profile?.acronym || '').trim(),
        websites,
        website: websites[0]?.url || '',
        phones,
        phone: phones[0]?.number || '',
        emails,
        email: emails[0]?.address || '',
        palette,
        scheme,
        primaryColor: palette[0] || PORTAL_COLORS.teal600,
        accentColor: palette[1] || palette[0] || PORTAL_COLORS.teal500,
        brandFill: scheme === THEME_SCHEME_GRADIENT
            ? buildGradientCss(palette, '135deg')
            : (palette[0] || PORTAL_COLORS.teal600),
        storageKey: key,
        cssVars: buildStatePortalThemeCssVars({ palette, scheme }),
    };
}

/** Normalize branding websites from legacy `website` string or `websites[]`. */
export function normalizeBrandingWebsites(seed = {}) {
    const fromList = Array.isArray(seed?.websites) ? seed.websites : null;
    if (fromList) {
        return fromList
            .map((entry) => {
                if (typeof entry === 'string') {
                    const url = normalizeWebsiteHref(entry);
                    return url ? { title: '', url } : null;
                }
                const url = normalizeWebsiteHref(entry?.url || entry?.href || '');
                if (!url) return null;
                return {
                    title: String(entry?.title || entry?.label || '').trim(),
                    url,
                    ip: String(entry?.ip || entry?.ipAddress || '').trim(),
                };
            })
            .filter(Boolean)
            .slice(0, 5);
    }
    const legacy = normalizeWebsiteHref(seed?.website || '');
    return legacy ? [{ title: '', url: legacy }] : [];
}

/** Normalize branding phones from `phones[]` or legacy `phone` string. */
export function normalizeBrandingPhones(seed = {}) {
    const fromList = Array.isArray(seed?.phones) ? seed.phones : null;
    if (fromList) {
        return fromList
            .map((entry) => {
                if (typeof entry === 'string') {
                    const number = String(entry || '').trim();
                    const countryCode = '+1';
                    const href = normalizePhoneHref(number, countryCode);
                    return href ? { title: '', number, countryCode, href } : null;
                }
                const number = String(entry?.number || entry?.phone || entry?.value || '').trim();
                const countryCode = String(entry?.countryCode || entry?.dialCode || '+1').trim() || '+1';
                const href = normalizePhoneHref(number || entry?.href || '', countryCode);
                if (!href) return null;
                return {
                    title: String(entry?.title || entry?.label || '').trim(),
                    number: number || href.replace(/^tel:/i, ''),
                    countryCode,
                    href,
                };
            })
            .filter(Boolean)
            .slice(0, 5);
    }
    const legacy = String(seed?.phone || '').trim();
    const countryCode = '+1';
    const href = normalizePhoneHref(legacy, countryCode);
    return href ? [{ title: '', number: legacy, countryCode, href }] : [];
}

export function normalizeWebsiteHref(value = '') {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^mailto:/i.test(raw) || /^tel:/i.test(raw)) return raw;
    return `https://${raw.replace(/^\/+/, '')}`;
}

export function normalizePhoneHref(value = '', countryCode = '') {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^tel:/i.test(raw)) return raw;
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    const dial = String(countryCode || '').trim();
    if (dial && dial !== '+') {
        const dialDigits = dial.replace(/\D/g, '');
        if (dialDigits && !digits.startsWith(dialDigits)) {
            return `tel:+${dialDigits}${digits}`;
        }
    }
    const compact = raw.replace(/[^\d+]/g, '');
    if (!compact || compact === '+') return '';
    return `tel:${compact.startsWith('+') ? compact : compact}`;
}

export function websiteDisplayHost(url = '') {
    const href = normalizeWebsiteHref(url);
    if (!href) return '';
    try {
        return new URL(href).host.replace(/^www\./i, '');
    } catch (_) {
        return href.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    }
}

export function phoneDisplayLabel(number = '') {
    return String(number || '').trim().replace(/^tel:/i, '');
}

/** Normalize branding emails from `emails[]` or legacy `email` string. */
export function normalizeBrandingEmails(seed = {}) {
    const fromList = Array.isArray(seed?.emails) ? seed.emails : null;
    if (fromList) {
        return fromList
            .map((entry) => {
                if (typeof entry === 'string') {
                    const address = emailDisplayLabel(entry);
                    const href = normalizeEmailHref(address);
                    return href ? { title: '', address, href } : null;
                }
                const address = emailDisplayLabel(entry?.address || entry?.email || entry?.value || '');
                const href = normalizeEmailHref(address || entry?.href || '');
                if (!href) return null;
                return {
                    title: String(entry?.title || entry?.label || '').trim(),
                    address: address || emailDisplayLabel(href),
                    href,
                };
            })
            .filter(Boolean)
            .slice(0, 5);
    }
    const legacy = emailDisplayLabel(seed?.email || '');
    const href = normalizeEmailHref(legacy);
    return href ? [{ title: '', address: legacy, href }] : [];
}

export function normalizeEmailHref(value = '') {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^mailto:/i.test(raw)) {
        const address = emailDisplayLabel(raw);
        return address.includes('@') ? `mailto:${address}` : '';
    }
    if (!raw.includes('@') || /\s/.test(raw)) return '';
    return `mailto:${raw}`;
}

export function emailDisplayLabel(email = '') {
    return String(email || '').trim().replace(/^mailto:/i, '');
}

/** Live US phone display formatting: (XXX) XXX-XXXX */
export function formatBrandingPhoneNumber(input = '') {
    const cleaned = String(input || '').replace(/\D/g, '').slice(0, 10);
    if (!cleaned) return '';
    const area = cleaned.slice(0, 3);
    const mid = cleaned.slice(3, 6);
    const last = cleaned.slice(6, 10);
    if (cleaned.length <= 3) return `(${area}`;
    if (cleaned.length <= 6) return `(${area}) ${mid}`;
    return `(${area}) ${mid}-${last}`;
}

/**
 * Patch and persist the marketing branding profile for the current org/state.
 * `mutator` receives a shallow-cloned profile and should return the next profile object.
 */
export function persistStatePortalBrandingProfile(mainStore = {}, state = '', mutator) {
    if (typeof mutator !== 'function' || typeof window === 'undefined') return null;
    const orgId = resolveBrandingOrgId(mainStore);
    const stateKey = String(state || mainStore?.currentStatePortal?.abbreviation || '').trim();
    const key = brandingStorageKey(orgId, stateKey);

    let parsed = {};
    try {
        const raw = window.localStorage.getItem(key);
        if (raw) {
            const next = JSON.parse(raw);
            if (next && typeof next === 'object') parsed = next;
        }
    } catch (_) {
        parsed = {};
    }

    const existingProfile = parsed.profile && typeof parsed.profile === 'object' ? parsed.profile : {};
    const existingConnectors = parsed.connectors && typeof parsed.connectors === 'object' ? parsed.connectors : {};
    const draftProfile = { ...existingProfile };
    const mutated = mutator(draftProfile);
    const nextProfile = mutated && typeof mutated === 'object' ? mutated : draftProfile;
    const websites = normalizeBrandingWebsites(nextProfile);
    const phones = normalizeBrandingPhones(nextProfile).map((phone) => ({
        ...phone,
        number: formatBrandingPhoneNumber(phone.number || phone.href || ''),
    }));
    const emails = normalizeBrandingEmails(nextProfile);
    const savedProfile = {
        ...nextProfile,
        websites,
        website: websites[0]?.url || '',
        phones,
        phone: phones[0]?.number || '',
        emails,
        email: emails[0]?.address || '',
    };

    const saved = persistBrandingRecord(key, {
        ...parsed,
        profile: savedProfile,
        connectors: existingConnectors,
    });
    if (!saved) return null;

    emitStatePortalBrandingUpdated({
        profile: savedProfile,
        connectors: saved.connectors,
        orgId,
        state: stateKey,
    });
    return savedProfile;
}

export function applyStatePortalTheme(branding = {}) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const vars = branding.cssVars || buildStatePortalThemeCssVars(branding);
    Object.entries(vars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
    root.dataset.statePortalScheme = normalizeThemeScheme(branding.scheme);
    root.dataset.statePortalTheme = 'on';
}

export function clearStatePortalTheme() {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    STATE_PORTAL_THEME_CSS_VARS.forEach((key) => {
        root.style.removeProperty(key);
    });
    delete root.dataset.statePortalScheme;
    delete root.dataset.statePortalTheme;
}

export function isStatePortalRoutePath(path = '') {
    return String(path || '').startsWith('/state-portal');
}

export function syncStatePortalThemeFromStore(mainStore = {}, routePath = '') {
    if (!isStatePortalRoutePath(routePath)) {
        clearStatePortalTheme();
        return null;
    }
    const branding = loadAppliedStatePortalBranding(
        mainStore,
        mainStore?.currentStatePortal?.abbreviation || '',
    );
    applyStatePortalTheme(branding);
    return branding;
}

/**
 * Keep State Portal teal chrome live with saved branding across SP routes.
 */
export function installStatePortalThemeSync({ router, getMainStore } = {}) {
    if (typeof window === 'undefined' || !router || typeof getMainStore !== 'function') {
        return () => {};
    }

    let lastPortalIdentity = '';

    const run = () => {
        try {
            const store = getMainStore();
            const path = router.currentRoute?.value?.path || '';
            syncStatePortalThemeFromStore(store, path);
            lastPortalIdentity = isStatePortalRoutePath(path)
                ? `${store?.currentStatePortal?.abbreviation || ''}|${resolveBrandingOrgId(store)}`
                : '';
        } catch (_) {
            /* pinia may not be ready yet */
        }
    };

    const afterEachOff = router.afterEach(() => {
        run();
    });
    const onBrandingUpdated = () => run();
    window.addEventListener(STATE_PORTAL_BRANDING_UPDATED, onBrandingUpdated);
    window.addEventListener('storage', onBrandingUpdated);

    let unsubscribeStore = null;
    try {
        const store = getMainStore();
        unsubscribeStore = store.$subscribe(() => {
            const path = router.currentRoute?.value?.path || '';
            if (!isStatePortalRoutePath(path)) return;
            const identity = `${store?.currentStatePortal?.abbreviation || ''}|${resolveBrandingOrgId(store)}`;
            if (identity === lastPortalIdentity) return;
            run();
        });
    } catch (_) {
        unsubscribeStore = null;
    }

    run();

    return () => {
        if (typeof afterEachOff === 'function') afterEachOff();
        if (typeof unsubscribeStore === 'function') unsubscribeStore();
        window.removeEventListener(STATE_PORTAL_BRANDING_UPDATED, onBrandingUpdated);
        window.removeEventListener('storage', onBrandingUpdated);
        clearStatePortalTheme();
    };
}

export function emitStatePortalBrandingUpdated(detail = {}) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(STATE_PORTAL_BRANDING_UPDATED, { detail }));
}

/** Ask Marketing Tools / Branding to open (optionally into the editor). */
export function emitStatePortalOpenBranding(detail = {}) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(STATE_PORTAL_OPEN_BRANDING, { detail: detail || {} }));
}

/** Generic host placeholder used only when no branding logo is on file. */
export const STATE_PORTAL_FALLBACK_IMAGE = 'https://host.refreps.com/images/placeholder-image.png';

/**
 * Organization branding logo for the current State Portal org/state.
 * Used as the universal cover when a resource/material has no image.
 */
export function resolveStatePortalBrandingLogoUrl(mainStore = {}, state = '') {
    const fromStore = String(loadAppliedStatePortalBranding(mainStore, state).logoUrl || '').trim();
    if (fromStore) return fromStore;
    // Fall back: scan branding keys for this state when org context is unavailable
    // (e.g. backpack helpers outside a Vue component).
    if (typeof window === 'undefined' || !window.localStorage) return '';
    const stateKey = slugPart(state || mainStore?.currentStatePortal?.abbreviation || '');
    const suffix = `.${stateKey}`;
    const prefix = 'statePortal.marketingBranding.';
    try {
        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index);
            if (!key || !key.startsWith(prefix) || !key.endsWith(suffix)) continue;
            const raw = window.localStorage.getItem(key);
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const logo = String(parsed?.profile?.logoUrl || '').trim();
            if (logo) return logo;
        }
    } catch (_) {
        /* ignore */
    }
    return '';
}

/**
 * Prefer an explicit material/resource image; otherwise the branding logo;
 * otherwise the generic platform placeholder.
 */
export function resolveStatePortalMaterialImage(image = '', mainStore = {}, state = '') {
    const provided = String(image || '').trim();
    if (provided) return provided;
    return resolveStatePortalBrandingLogoUrl(mainStore, state) || STATE_PORTAL_FALLBACK_IMAGE;
}

/** Image to use when a material/resource has no cover of its own. */
export function statePortalMaterialImageFallbackSrc(mainStore = {}, state = '') {
    return resolveStatePortalBrandingLogoUrl(mainStore, state) || STATE_PORTAL_FALLBACK_IMAGE;
}

/**
 * `<img @error>` handler: try branding logo once, then generic placeholder.
 * Prevents infinite error loops when the brand logo itself fails to load.
 */
export function applyStatePortalMaterialImageFallback(event, mainStore = {}, state = '') {
    const el = event?.target;
    if (!el || typeof el !== 'object') return;
    const stage = String(el.dataset?.spImgFallback || '');
    const brandLogo = resolveStatePortalBrandingLogoUrl(mainStore, state);
    const current = String(el.currentSrc || el.src || '').trim();
    if (!stage && brandLogo && current !== brandLogo) {
        el.dataset.spImgFallback = 'brand';
        el.src = brandLogo;
        return;
    }
    if (el.dataset) el.dataset.spImgFallback = 'default';
    if (current !== STATE_PORTAL_FALLBACK_IMAGE) {
        el.src = STATE_PORTAL_FALLBACK_IMAGE;
    }
}

